import React from "react";
import { useQueryClient } from "react-query";
import { generateSignature, uploadResult } from "./uploadWidget.biz";

interface Result {
  event: string;
  info: { secure_url: string };
}

interface Data {
  email: string;
  dealerLogo?: string;
}

function useUploadSelector(
  data: Data,
  createAcct: boolean,
  setUser: React.Dispatch<React.SetStateAction<any>> | null
) {
  const queryClient = useQueryClient();
  const widgetRef = React.useRef<any>(null);

  const checkUploadResult = async (result: Result) => {
    // Ignore non-success events
    if (result?.event !== "success") return;

    const uResult = await uploadResult(result, createAcct || false);

    if (uResult && !createAcct) {
      await queryClient.invalidateQueries("UserAccount");
    }

    if (uResult && createAcct && setUser) {
      setUser(prev => ({ ...prev, dealerLogo: uResult }));
    }
  };

  const ensureWidget = () => {
    if (widgetRef.current) return widgetRef.current;

    // Create once, lazily
    widgetRef.current = (window as any).cloudinary.createUploadWidget(
      {
        folder: "DealerLogos",
        uploadPreset: "pbeo7xvi",
        cloudName: "ag-millworks",
        sources: ["local", "url", "image_search"],
        apiKey: "762425355781477",
        clientAllowedFormats: ["webp", "jpg", "png", "jpeg"],
        maxFileSize: 2_500_000,
        publicId: data.email,
        uploadSignature: generateSignature,
      },
      (error: any, result: Result) => {
        if (error) {
          console.error(error);
          return;
        }
        // Process only on success
        if (result?.event === "success") {
          checkUploadResult(result);
        }
      }
    );

    return widgetRef.current;
  };

  const showWidget = () => {
    if (!data.email) return; // require an email first
    const widget = ensureWidget();
    // Update publicId if email changed since first init
    try {
      widget.update({ publicId: data.email });
    } catch {}
    widget.open();
  };

  return { showWidget };
}

export default useUploadSelector;
